import React, { useCallback, useState } from 'react'
import Offcanvas from 'react-bootstrap/Offcanvas'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { HouseConsignmentFormHandle } from './types'
import { notifyHouseConsignmentSaveResult } from './notifyHouseConsignmentSaveResult'

interface HouseConsignmentDrawerProps {
  isOpen: boolean
  houseConsignmentSequenceNumber: number
  formRef: React.RefObject<HouseConsignmentFormHandle | null>
  onClose: () => void
  children: React.ReactNode
}

function HouseConsignmentDrawer({
  isOpen,
  houseConsignmentSequenceNumber,
  formRef,
  onClose,
  children,
}: Readonly<HouseConsignmentDrawerProps>) {
  const { t } = useTranslation()
  const [isSaving, setIsSaving] = useState(false)

  const saveAndClose = useCallback(async () => {
    if (formRef.current?.isDirty()) {
      setIsSaving(true)
      try {
        const result = await formRef.current.save()
        notifyHouseConsignmentSaveResult(result, t)
        if (!result.success) return
      } finally {
        setIsSaving(false)
      }
    }
    onClose()
  }, [formRef, onClose, t])

  return (
    <Offcanvas
      className="extra-wide hc-drawer"
      show={isOpen}
      onHide={saveAndClose}
      placement="end"
    >
      <Offcanvas.Header
        className="border-bottom"
        closeButton
      >
        <h1 className="fs-4 fw-normal mb-0 text-dark">
          {t('declaration.p5.houseConsignmentDrawer.editTitle')}
          <span className="text-muted fs-5 ms-3">
            {`${t('declaration.p5.sequenceNumber')} ${houseConsignmentSequenceNumber}`}
          </span>
        </h1>
      </Offcanvas.Header>

      <Offcanvas.Body key="data-body" className="p-0">
        <div className="container-fluid hc-drawer__body">
          {children}
        </div>
      </Offcanvas.Body>

      <div className="hc-drawer__footer">
        <Button
          variant="dark"
          onClick={saveAndClose}
          disabled={isSaving}
        >
          {t('buttons.done')}
        </Button>
      </div>
    </Offcanvas>
  )
}

export default HouseConsignmentDrawer
